import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

type PageHeaderVariant = 'dark' | 'light' | 'brand'

interface PageHeaderProps {
  title: string
  subtitle?: string
  variant?: PageHeaderVariant
  eyebrow?: string
  children?: ReactNode
}

/**
 * PageHeader — standardized industrial hero used on every non-home page.
 *
 * No photographic backgrounds: the atmosphere comes from an engineered
 * composition — blueprint grid, dual brand-blue glow, diagonal accent stripe,
 * coordinate ticks and a corner "spec stamp". Height is fixed across pages
 * so navigation feels consistent.
 *
 * Variants:
 * - dark  (default): white text, deep ink background, blueprint grid
 * - brand          : white text on solid steel-blue with same overlays
 * - light          : charcoal text on paper-tone background
 */
export function PageHeader({
  title,
  subtitle,
  variant = 'dark',
  eyebrow,
  children,
}: PageHeaderProps) {
  const { t } = useTranslation()
  const isDark = variant === 'dark'
  const isBrand = variant === 'brand'
  const isLight = variant === 'light'

  return (
    <section
      aria-labelledby="page-header-title"
      className={cn(
        'relative isolate w-full overflow-hidden flex items-end',
        'min-h-[380px] md:min-h-[460px]',
        'pt-28 md:pt-36 pb-14 md:pb-18',
        isDark && 'bg-ink-900 text-white',
        isBrand && 'bg-brand-700 text-white',
        isLight && 'bg-ink-100 text-ink-800'
      )}
    >
      {/* ── Base gradient wash ───────────────────────────────────────── */}
      <div
        aria-hidden="true"
        className={cn(
          'absolute inset-0 -z-20',
          isDark &&
            'bg-[radial-gradient(ellipse_at_top_right,theme(colors.brand.800)_0%,transparent_55%),radial-gradient(ellipse_at_bottom_left,theme(colors.ink.950)_10%,transparent_60%)]',
          isBrand &&
            'bg-[radial-gradient(ellipse_at_top_right,theme(colors.brand.500)_0%,transparent_55%),radial-gradient(ellipse_at_bottom_left,theme(colors.brand.900)_10%,transparent_60%)]',
          isLight &&
            'bg-[radial-gradient(ellipse_at_top_right,theme(colors.brand.100)_0%,transparent_55%),radial-gradient(ellipse_at_bottom_left,theme(colors.ink.200)_10%,transparent 60%)]'
        )}
      />

      {/* ── Blueprint fine grid ──────────────────────────────────────── */}
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 -z-10',
          isLight ? 'opacity-[0.18]' : 'opacity-[0.07]'
        )}
        style={{
          backgroundImage: `
            linear-gradient(to right, currentColor 1px, transparent 1px),
            linear-gradient(to bottom, currentColor 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
          color: isLight ? 'rgb(74, 155, 220)' : '#ffffff',
          maskImage:
            'radial-gradient(ellipse 80% 70% at 50% 50%, black 40%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 70% at 50% 50%, black 40%, transparent 100%)',
        }}
      />

      {/* ── Blueprint major grid (brand-tinted) ──────────────────────── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18]"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgb(74 155 220 / 0.55) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(74 155 220 / 0.55) 1px, transparent 1px)
          `,
          backgroundSize: '192px 192px',
        }}
      />

      {/* ── Radial brand glow (top-right) ────────────────────────────── */}
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -top-32 -right-24 -z-10 h-[28rem] w-[28rem] rounded-full blur-3xl',
          isDark && 'bg-brand-500/25',
          isBrand && 'bg-white/10',
          isLight && 'bg-brand-300/50'
        )}
      />

      {/* ── Radial brand glow (bottom-left) ──────────────────────────── */}
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -bottom-40 -left-32 -z-10 h-[30rem] w-[30rem] rounded-full blur-3xl',
          isDark && 'bg-brand-800/50',
          isBrand && 'bg-brand-900/50',
          isLight && 'bg-ink-300/40'
        )}
      />

      {/* ── Diagonal engineering stripe (top-right corner marker) ────── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rotate-45 opacity-40 fx-diagonal-accent"
      />

      {/* ── Noise / grain texture ────────────────────────────────────── */}
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 -z-10 mix-blend-overlay',
          isLight ? 'opacity-[0.15]' : 'opacity-[0.06]'
        )}
        style={{
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 140 140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
        }}
      />

      {/* ── Top decoration row (anchored to section, NOT to content) ─ */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-28 md:top-36"
      >
        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Left engineering tick rail */}
          <div
            className={cn(
              'absolute left-4 sm:left-6 lg:left-8 top-0 flex flex-col gap-1.5',
              isLight ? 'text-brand-500' : 'text-brand-400'
            )}
          >
            <span className="block h-3 w-px bg-current" />
            <span className="block h-6 w-px bg-current" />
            <span className="block h-3 w-px bg-current" />
          </div>

          {/* Corner "spec stamp" top-right */}
          <div
            className={cn(
              'hidden md:flex absolute right-4 sm:right-6 lg:right-8 top-0 flex-col items-end gap-1.5 text-[10px] font-bold uppercase tracking-[0.32em] tabular-nums',
              isLight ? 'text-ink-500' : 'text-white/55'
            )}
          >
            <span className="flex items-center gap-2">
              <span className={cn('block h-px w-10', isLight ? 'bg-brand-500' : 'bg-brand-400')} />
              {t('pageHeader.fenixBadge')}
            </span>
            <span className="flex items-center gap-2">
              <span className={cn('block h-px w-6', isLight ? 'bg-ink-400' : 'bg-white/30')} />
              {t('pageHeader.tagline')}
            </span>
          </div>
        </div>
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="pl-6 sm:pl-8 lg:pl-10 max-w-4xl">
          {eyebrow && (
            <p
              className={cn(
                'mb-5 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.26em]',
                isLight ? 'text-brand-600' : 'text-brand-300'
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'block h-px w-8',
                  isLight ? 'bg-brand-500' : 'bg-brand-400'
                )}
              />
              {eyebrow}
            </p>
          )}

          <h1
            id="page-header-title"
            className={cn(
              'fx-text-balance font-bold',
              'text-3xl sm:text-4xl md:text-5xl',
              'leading-[1.1]',
              isLight ? 'text-ink-900' : 'text-white'
            )}
          >
            {title}
          </h1>

          {subtitle && (
            <p
              className={cn(
                'mt-5 fx-text-pretty text-base sm:text-lg leading-relaxed max-w-2xl',
                isLight ? 'text-ink-600' : 'text-white/85'
              )}
            >
              {subtitle}
            </p>
          )}

          {children && <div className="mt-8">{children}</div>}
        </div>

        {/* ── Bottom brand accent line ───────────────────────────────── */}
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-4 sm:left-6 lg:left-8 h-[3px] w-24 bg-brand-500"
        />
      </div>
    </section>
  )
}
